import { Box, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import {
  blue,
  cyan,
  green,
  indigo,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
  grey,
} from "@mui/material/colors";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { WasteSubmissionsStaffSummaryQuery } from "generated-graphql/graphql";
import { useMemo } from "react";

type Props = {
  data?: WasteSubmissionsStaffSummaryQuery["wasteSubmissionsStaffSummary"];
  loading: boolean;
  onAssigneeClick?: (assigneeEmail: string) => void;
  selectedAssigneeEmail?: string | null;
};

// Color palette for assignees (cycling through colors)
const ASSIGNEE_COLORS = [
  blue[400],
  green[400],
  orange[400],
  purple[400],
  red[400],
  teal[400],
  pink[400],
  cyan[400],
  yellow[600],
  indigo[400],
];

const ASSIGNEE_COLORS_INACTIVE = [
  blue[100],
  green[100],
  orange[100],
  purple[100],
  red[100],
  teal[100],
  pink[100],
  cyan[100],
  yellow[100],
  indigo[100],
];

export function AssigneeDonutChart({
  data,
  loading,
  onAssigneeClick,
  selectedAssigneeEmail,
}: Props) {
  const theme = useTheme();

  const hasSelection = selectedAssigneeEmail != null;

  const assignees = useMemo(() => {
    if (!data) return [];
    return data.byAssignee;
  }, [data]);

  const selectedIndex = useMemo(() => {
    if (!selectedAssigneeEmail) return null;
    // Handle "unassigned" special case - matches assigneeId instead of email
    const index = assignees.findIndex((a) =>
      selectedAssigneeEmail.toLowerCase() === "unassigned"
        ? a.assigneeId === "unassigned"
        : a.assigneeEmail === selectedAssigneeEmail
    );
    return index >= 0 ? index : null;
  }, [selectedAssigneeEmail, assignees]);

  const totalSubmissions = useMemo(() => {
    if (!data) return 0;

    if (hasSelection && selectedAssigneeEmail) {
      // Handle "unassigned" special case - matches assigneeId instead of email
      const assigneeData = assignees.find((a) =>
        selectedAssigneeEmail.toLowerCase() === "unassigned"
          ? a.assigneeId === "unassigned"
          : a.assigneeEmail === selectedAssigneeEmail
      );
      return assigneeData?.count ?? 0;
    }

    // Sum all assignee counts
    return assignees.reduce((sum, a) => sum + a.count, 0);
  }, [data, hasSelection, selectedAssigneeEmail, assignees]);

  const seriesData = useMemo(() => {
    if (!assignees.length) return [];

    return assignees.map((assignee, index) => {
      const isSelected = !hasSelection || selectedAssigneeEmail === assignee.assigneeEmail;
      const isUnassigned = assignee.assigneeId === "unassigned";
      const colorIndex = index % ASSIGNEE_COLORS.length;

      return {
        id: assignee.assigneeEmail ?? assignee.assigneeId, // Use email for filtering, fall back to ID for unassigned
        value: assignee.count,
        color: isUnassigned
          ? isSelected
            ? grey[400]
            : grey[200]
          : isSelected
            ? ASSIGNEE_COLORS[colorIndex]
            : ASSIGNEE_COLORS_INACTIVE[colorIndex],
        label: assignee.assigneeName,
      };
    });
  }, [assignees, hasSelection, selectedAssigneeEmail]);

  const legendItems = useMemo(() => {
    return seriesData.map((item, index) => {
      const isSelected = !hasSelection || selectedAssigneeEmail === item.id;
      const isUnassigned = item.id === "unassigned";
      const colorIndex = index % ASSIGNEE_COLORS.length;

      return {
        color: isUnassigned ? grey[400] : ASSIGNEE_COLORS[colorIndex],
        isSelected,
        value: (
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            gap={2}
            sx={{ opacity: isSelected ? 1 : 0.4 }}
          >
            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.value.toLocaleString()}
            </Typography>
          </Box>
        ),
      };
    });
  }, [seriesData, hasSelection, selectedAssigneeEmail]);

  const handleAssigneeClick = (item: DefaultizedPieValueType) => {
    if (onAssigneeClick) {
      onAssigneeClick(item.id as string); // This is now email
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
        minHeight: 380,
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            By Staff Assignee
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="circular"
                width={250}
                height={250}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>
            <Stack spacing={1} sx={{ flex: 1 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-10px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" sx={{ flex: 1 }} height={20} />
                  <Skeleton variant="text" width={40} height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            By Staff Assignee
          </Typography>
          {assignees.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No data available
            </Typography>
          ) : (
            <DonutChart
              seriesData={seriesData}
              selectedIndex={selectedIndex}
              label={totalSubmissions.toLocaleString()}
              subLabel={hasSelection ? "Assignments" : "Total"}
              containerHeight="18rem"
              legend={{
                direction: "column",
                position: "right",
              }}
              legendItems={legendItems}
              onItemClick={(event, pieItemIdentifier, item) => handleAssigneeClick(item)}
              onLegendItemClick={(item) => handleAssigneeClick(item)}
            />
          )}
        </Box>
      )}
    </Paper>
  );
}
